import React, { useEffect, useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';

import DepositInvoiceModal from '../components/DepositInvoiceModal';
import CreateEstimateModal from '../components/CreateEstimateModal';
import axios, { apiBaseUrl } from '../api/httpClient';
import {
  InvoiceAllocationResponse,
  InvoiceEstimateAllocation,
  InvoiceLinkOrigin,
  ReconciliationStatus,
} from '../types/invoiceAllocations';

interface Project {
  id: number;
  name: string;
  url?: string;     
  contactUrl?: string;
  status?: string;
}

export interface Invoice {
  id?: number;
  url: string;
  reference?: string | null;
  status: string;
  totalValue: string;
  netValue: string;
  salesTaxValue?: string;
  allocationId?: number | null;
  sourceId?: number | null;
  batchKey?: string | null;
  origin?: InvoiceLinkOrigin;
  rawProjectId?: number | null;
  effectiveProjectId?: number;
  estimateId?: number;
  estimateProjectId?: number | null;
  allocatedNet?: string;
  allocatedTax?: string;
  allocatedGross?: string;
  sourceNet?: string;
  sourceTax?: string;
  sourceGross?: string;
  reconciliationGroup?: string | null;
  reconciliationStatus?: ReconciliationStatus;
  groupMembers?: number[];
  crossProject?: boolean;
  projectlessEstimate?: boolean;
  evidenceSummary?: string;
  comments?: string | null;
  poReference?: string | null;
  sourceFingerprint?: string;
}

export interface Estimate {
  id: number;
  projectId?: number | null;
  reference: string;
  status: string;
  contactUrl: string;   
  currency: string;     
  netValue: string;     
  invoices: Invoice[];
}

interface ProjectItem extends Project {
  expanded?: boolean;
  loadingEstimates?: boolean;
  estimates?: Estimate[];
  allocationConflicts?: Array<Record<string, unknown>>;
}

interface ProjectDetails {
  id: number;
  estimates: Estimate[];
  invoiceAllocationEvidence?: InvoiceAllocationResponse;
  invoiceReconciliation?: InvoiceAllocationResponse['meta'];
}

const allocationToInvoice = (allocation: InvoiceEstimateAllocation): Invoice => ({
  id: allocation.sourceInvoiceId,
  url: allocation.sourceInvoiceUrl,
  reference: allocation.sourceInvoiceReference,
  status: allocation.sourceInvoiceStatus ?? '',
  totalValue: allocation.sourceGross,
  netValue: allocation.sourceNet,
  salesTaxValue: allocation.sourceTax,
  allocationId: allocation.allocationId,
  sourceId: allocation.sourceId,
  batchKey: allocation.batchKey,
  origin: allocation.origin,
  rawProjectId: allocation.rawProjectId,
  effectiveProjectId: allocation.effectiveProjectId,
  estimateId: allocation.estimateId,
  estimateProjectId: allocation.estimateProjectId,
  allocatedNet: allocation.allocatedNet,
  allocatedTax: allocation.allocatedTax,
  allocatedGross: allocation.allocatedGross,
  sourceNet: allocation.sourceNet,
  sourceTax: allocation.sourceTax,
  sourceGross: allocation.sourceGross,
  reconciliationGroup: allocation.reconciliationGroup,
  reconciliationStatus: allocation.reconciliationStatus,
  groupMembers: allocation.groupMembers,
  crossProject: allocation.crossProject,
  projectlessEstimate: allocation.projectlessEstimate,
  evidenceSummary: allocation.evidenceSummary,
  comments: allocation.comments,
  poReference: allocation.poReference,
  sourceFingerprint: allocation.sourceFingerprint,
});

const invoiceKey = (invoice: Invoice): string => invoice.allocationId
  ? `allocation-${invoice.allocationId}`
  : `invoice-${invoice.id ?? invoice.url}-${invoice.origin ?? 'project'}`;

export const mergeAllocationEvidence = (
  estimates: Estimate[],
  allocations: InvoiceEstimateAllocation[]
): Estimate[] => estimates.map((estimate) => {
  const merged = new Map<string, Invoice>();

  (estimate.invoices ?? []).forEach((invoice) => merged.set(invoiceKey(invoice), invoice));
  allocations
    .filter((allocation) => allocation.estimateId === estimate.id)
    .map(allocationToInvoice)
    .forEach((invoice) => merged.set(invoiceKey(invoice), invoice));

  return {
    ...estimate,
    invoices: Array.from(merged.values()).sort((left, right) =>
      (left.id ?? 0) - (right.id ?? 0) || (left.allocationId ?? 0) - (right.allocationId ?? 0)
    ),
  };
});

const money = (value: string | undefined, currency = 'GBP'): string =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(Number(value ?? 0));

const originLabel = (origin?: InvoiceLinkOrigin): string => {
  if (origin === 'reviewed_manifest') return 'Reviewed allocation';
  if (origin === 'native') return 'Native FreeAgent link';
  if (origin === 'legacy_marker') return 'Legacy marker';
  return 'Invoice';
};

const FreeAgentProjectsPage: React.FC = () => {
  const [projects, setProjects] = useState<ProjectItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loadingProjects, setLoadingProjects] = useState<boolean>(false);
  const [includeInactiveProjects, setIncludeInactiveProjects] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');

  // For the deposit invoice modal:
  const [depositModalVisible, setDepositModalVisible] = useState(false);
  const [activeEstimate, setActiveEstimate] = useState<Estimate | null>(null);
  const [activeProjectId, setActiveProjectId] = useState<number | null>(null);

  // For the final invoice spinner
  const [finalInvoiceLoading, setFinalInvoiceLoading] = useState<{ [estimateId: number]: boolean }>({});

  // For the "Create Estimate" modal
  const [createEstimateModalVisible, setCreateEstimateModalVisible] = useState(false);
  const [activeProjectForEstimate, setActiveProjectForEstimate] = useState<ProjectItem | null>(null);

  // ** NEW: Track if we're loading on the "Create Estimate" call
  const [creatingEstimate, setCreatingEstimate] = useState(false);

  // 1) On mount, load basic project list
  useEffect(() => {
    let cancelled = false;

    setLoadingProjects(true);
    setError(null);

    axios
      .get<ProjectItem[]>(`${apiBaseUrl}/freeagent-projects`, {
        params: {
          onlyActive: !includeInactiveProjects,
          includeCustomerSupportProjects: false,
        },
      })
      .then((res) => {
        if (!cancelled) setProjects(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error fetching projects:', err);
        setError('Failed to load projects');
      })
      .finally(() => {
        if (!cancelled) setLoadingProjects(false);
      });

    return () => {
      cancelled = true;
    };
  }, [includeInactiveProjects]);

  // 2) Load or refresh estimates for a given project
  const handleLoadEstimates = (projectId: number) => {
    setError(null);
    setProjects((prev) =>
      prev.map((p) =>
        p.id === projectId ? { ...p, loadingEstimates: true } : p
      )
    );

    axios
      .get<ProjectDetails>(`${apiBaseUrl}/freeagent-projects/${projectId}`)
      .then((projectResponse) => {
        const allocationEvidence = projectResponse.data.invoiceAllocationEvidence;
        const estimates = mergeAllocationEvidence(
          projectResponse.data.estimates,
          allocationEvidence?.data ?? []
        );
        setProjects((prev) =>
          prev.map((p) =>
            p.id === projectId
              ? {
                  ...p,
                  estimates,
                  allocationConflicts: allocationEvidence?.meta.conflicts
                    ?? projectResponse.data.invoiceReconciliation?.conflicts
                    ?? [],
                  expanded: true,
                  loadingEstimates: false,
                }
              : p
          )
        );
      })
      .catch((err) => {
        console.error('Error fetching project details:', err);
        setError('Failed to load project allocation evidence');
        setProjects((prev) =>
          prev.map((p) =>
            p.id === projectId ? { ...p, loadingEstimates: false } : p
          )
        );
      });
  };

  // 3) Toggle expand
  const toggleProjectExpand = (projectId: number) => {
    setProjects((prev) =>
      prev.map((p) =>
        p.id === projectId ? { ...p, expanded: !p.expanded } : p
      )
    );
  };

  // 4) Show deposit modal
  const handleShowDepositModal = (estimate: Estimate, projectId: number) => {
    setActiveEstimate(estimate);
    setActiveProjectId(projectId);
    setDepositModalVisible(true);
  };

  // 5) Called when the deposit form is submitted
  const handleCreateDepositInvoice = async (formData: {
    contact: string;
    amount: number;
  }) => {
    if (!activeEstimate || !activeProjectId) return;

    try {
      const payload = {
        instalmentEstimate: activeEstimate.id,
        contact: formData.contact,
        invoiceItems: [
          {
            description: 'Deposit Payment',
            itemType: 'Services',
            price: formData.amount,
            quantity: 1,
            salesTaxRate: 20,
            salesTaxStatus: 'TAXABLE',
          },
        ],
      };

      const resp = await axios.post(
        `${apiBaseUrl}/freeagent-invoices`,
        payload
      );

      console.log('Deposit invoice created:', resp.data);
      setDepositModalVisible(false);

      handleLoadEstimates(activeProjectId);
    } catch (err) {
      console.error('Error creating deposit invoice:', err);
      setError('Could not create deposit invoice.');
    }
  };

  // 6) Create final invoice immediately
  const createFinalInvoice = (estimateId: number, projectId: number) => {
    setError(null);
    setFinalInvoiceLoading((prev) => ({ ...prev, [estimateId]: true }));

    axios
      .post(
        `${apiBaseUrl}/freeagent-invoices`,
        {},
        {
          params: { estimateId },
        }
      )
      .then((resp) => {
        console.log('Final invoice created:', resp.data);
        handleLoadEstimates(projectId);
      })
      .catch((err) => {
        console.error('Error creating final invoice:', err);
        setError('Could not create final invoice');
      })
      .finally(() => {
        setFinalInvoiceLoading((prev) => ({ ...prev, [estimateId]: false }));
      });
  };

  // 7) Show the "Create Estimate" modal
  const handleShowCreateEstimateModal = (project: ProjectItem) => {
    setActiveProjectForEstimate(project);
    setCreateEstimateModalVisible(true);
  };

  // 8) Submit new estimate
  const handleCreateEstimate = async (formData: {
    netValue: string;
    datedOn: string;
    status: string;
  }) => {
    if (!activeProjectForEstimate) return;

    // Turn on the spinner for "Create Estimate"
    setCreatingEstimate(true);

    try {
      const projectUrl = activeProjectForEstimate.url
        ? activeProjectForEstimate.url
        : `https://api.freeagent.com/v2/projects/${activeProjectForEstimate.id}`;

      const contactUrl = activeProjectForEstimate.contactUrl || '';

      const payload = {
        contactUrl,
        project: projectUrl,
        status: formData.status,
        netValue: formData.netValue,
        datedOn: formData.datedOn,
        // If you want to pass reference or other fields, do so here
      };

      const resp = await axios.post(
        `${apiBaseUrl}/freeagent-estimates`,
        payload
      );
      console.log('Estimate created:', resp.data);

      // Hide modal
      setCreateEstimateModalVisible(false);

      // Re-fetch the estimates so we see the newly created estimate
      handleLoadEstimates(activeProjectForEstimate.id);
    } catch (err) {
      console.error('Error creating estimate:', err);
      setError('Could not create new estimate.');
    } finally {
      // Turn off spinner
      setCreatingEstimate(false);
    }
  };

  const normalizedProjectSearch = projectSearch.trim().toLowerCase();
  const visibleProjects = projects.filter((project) =>
    normalizedProjectSearch === ''
    || project.name.toLowerCase().includes(normalizedProjectSearch)
    || String(project.id).includes(normalizedProjectSearch)
  );

  return (
    <div className="container mt-3">
      <h1>FreeAgent Projects</h1>
      {error && <Alert variant="danger">{error}</Alert>}

      <div className="row g-3 align-items-end mb-3">
        <div className="col-md-7">
          <label className="form-label" htmlFor="project-search">Search projects</label>
          <input
            id="project-search"
            className="form-control"
            type="search"
            value={projectSearch}
            placeholder="Name or FreeAgent project ID"
            onChange={(event) => setProjectSearch(event.target.value)}
          />
        </div>
        <div className="col-md-5">
          <div className="form-check">
            <input
              id="include-inactive-projects"
              className="form-check-input"
              type="checkbox"
              checked={includeInactiveProjects}
              onChange={(event) => setIncludeInactiveProjects(event.target.checked)}
            />
            <label className="form-check-label" htmlFor="include-inactive-projects">
              Include inactive projects
            </label>
          </div>
          <div className="form-text">
            Use this to review historical invoice allocations.
          </div>
        </div>
      </div>

      {loadingProjects && (
        <div className="text-center my-3">
          <Spinner animation="border" />
        </div>
      )}

      {!loadingProjects && projects.length === 0 && <p>No projects found.</p>}

      {!loadingProjects && projects.length > 0 && (
        <p className="text-muted" aria-live="polite">
          Showing {visibleProjects.length} of {projects.length}{' '}
          {includeInactiveProjects ? 'active and inactive' : 'active'} projects.
        </p>
      )}

      {!loadingProjects && projects.length > 0 && visibleProjects.length === 0 && (
        <p>No projects match your search.</p>
      )}

      {/* Display projects */}
      {!loadingProjects &&
        visibleProjects.map((project) => (
          <div className="card mb-3" key={project.id}>
            <div className="card-body">
              <h5 className="card-title d-flex justify-content-between">
                <span>
                  {project.name}
                  {includeInactiveProjects
                    && project.status
                    && project.status.toLowerCase() !== 'active' && (
                      <span className="badge bg-secondary ms-2">{project.status}</span>
                  )}
                </span>
                {project.estimates && (
                  <button
                    className="btn btn-link btn-sm"
                    onClick={() => toggleProjectExpand(project.id)}
                  >
                    {project.expanded ? 'Hide' : 'Show'}
                  </button>
                )}
              </h5>

              {/* Create Estimate button */}
              <button
                className="btn btn-sm btn-secondary me-2"
                onClick={() => handleShowCreateEstimateModal(project)}
              >
                Create Estimate
              </button>

              <button
                className="btn btn-sm btn-primary"
                disabled={project.loadingEstimates}
                onClick={() => handleLoadEstimates(project.id)}
              >
                {project.loadingEstimates ? 'Loading...' : 'Load/Refresh Estimates'}
              </button>

              {/* If estimates are loaded & expanded, show them */}
              {project.estimates && project.expanded && (
                <div className="mt-3">
                  {(project.allocationConflicts?.length ?? 0) > 0 && (
                    <Alert variant="warning">
                      Some native and legacy invoice links conflict. No duplicate allocation has been counted.
                    </Alert>
                  )}
                  {project.loadingEstimates ? (
                    <Spinner animation="border" />
                  ) : project.estimates.length > 0 ? (
                    <ul>
                      {project.estimates.map((est) => {
                        const estimateNetVal = parseFloat(est.netValue || '0');
                        const distinctInvoices = new Map<string, Invoice>();
                        est.invoices.forEach((invoice) => distinctInvoices.set(invoiceKey(invoice), invoice));
                        const sumOfInvoiceNetVals = Array.from(distinctInvoices.values()).reduce((acc, inv) => {
                          const invVal = parseFloat(inv.allocatedNet || inv.netValue || '0');
                          return acc + invVal;
                        }, 0);
                        const groupEvidence = est.invoices.find((invoice) => invoice.reconciliationGroup);
                        const isGroupReconciled = groupEvidence?.reconciliationStatus === 'reconciled_as_group';
                        const isFullyInvoiced = groupEvidence
                          ? isGroupReconciled
                          : sumOfInvoiceNetVals >= estimateNetVal;
                        const isLoadingFinalInvoice = !!finalInvoiceLoading[est.id];

                        return (
                          <li key={est.id} style={{ marginBottom: '1rem' }}>
                            <strong>Estimate {est.reference}</strong> ({est.status})
                            {groupEvidence ? (
                              <span className={`badge ms-2 ${isGroupReconciled ? 'bg-success' : 'bg-warning text-dark'}`}>
                                {isGroupReconciled ? 'Reconciled as group' : 'Group reconciliation pending'}
                              </span>
                            ) : isFullyInvoiced ? (
                              <span className="badge bg-success ms-2">Fully invoiced</span>
                            ) : null}
                            {(est.projectId == null || est.invoices.some((invoice) => invoice.projectlessEstimate)) && (
                              <span className="badge bg-info text-dark ms-2">Projectless estimate</span>
                            )}
                            <br />

                            <button
                              className="btn btn-sm btn-warning me-2"
                              onClick={() => handleShowDepositModal(est, project.id)}
                              disabled={isFullyInvoiced}
                            >
                              Deposit Invoice
                            </button>

                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => createFinalInvoice(est.id, project.id)}
                              disabled={isFullyInvoiced || isLoadingFinalInvoice}
                            >
                              {isLoadingFinalInvoice ? (
                                <>
                                  <Spinner
                                    animation="border"
                                    size="sm"
                                    style={{ marginRight: '5px', verticalAlign: 'middle' }}
                                  />
                                  Creating...
                                </>
                              ) : (
                                'Final Invoice'
                              )}
                            </button>

                            {/* Show existing invoices */}
                            {est.invoices?.length > 0 && (
                              <div style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                                <p className="mb-1"><strong>Invoice allocation evidence (read-only)</strong></p>
                                <ul className="list-unstyled">
                                  {est.invoices.map((inv) => (
                                    <li className="border rounded p-2 mb-2" key={invoiceKey(inv)}>
                                      <div>
                                        <a href={inv.url} target="_blank" rel="noreferrer">
                                          Invoice {inv.reference || inv.id}
                                        </a>{' '}
                                        <span className="badge bg-secondary">{originLabel(inv.origin)}</span>{' '}
                                        <span>{inv.status}</span>
                                      </div>
                                      <div>
                                        Allocated: {money(inv.allocatedNet || inv.netValue, est.currency)} net /{' '}
                                        {money(inv.allocatedGross || inv.totalValue, est.currency)} gross
                                      </div>
                                      {inv.sourceNet && inv.allocatedNet && inv.sourceNet !== inv.allocatedNet && (
                                        <div className="text-muted">
                                          Split from source invoice total {money(inv.sourceNet, est.currency)} net.
                                        </div>
                                      )}
                                      {(inv.rawProjectId || inv.effectiveProjectId) && (
                                        <div className="text-muted">
                                          Raw project: {inv.rawProjectId ?? 'none'}; effective project: {inv.effectiveProjectId ?? 'none'}.
                                        </div>
                                      )}
                                      {inv.crossProject && (
                                        <Alert variant="warning" className="py-1 px-2 mt-1 mb-1">
                                          Cross-project allocation — revenue is attributed once to effective project {inv.effectiveProjectId}.
                                        </Alert>
                                      )}
                                      {inv.reconciliationGroup && (
                                        <div>
                                          Group {inv.reconciliationGroup}: {inv.reconciliationStatus === 'reconciled_as_group'
                                            ? 'Reconciled as group'
                                            : 'Not reconciled'}.
                                        </div>
                                      )}
                                      {inv.evidenceSummary && <div className="text-muted">{inv.evidenceSummary}</div>}
                                      {inv.comments && <div>Comment: {inv.comments}</div>}
                                      {inv.poReference && <div>PO: {inv.poReference}</div>}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p>No estimates found.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}

      {/* Deposit Invoice Modal */}
      {activeEstimate && (
        <DepositInvoiceModal
          show={depositModalVisible}
          onHide={() => setDepositModalVisible(false)}
          defaultContactUrl={activeEstimate.contactUrl || ''}
          defaultCurrency={activeEstimate.currency || 'GBP'}
          onSubmit={handleCreateDepositInvoice}
        />
      )}

      {/* Create Estimate Modal */}
      <CreateEstimateModal
        show={createEstimateModalVisible}
        onHide={() => setCreateEstimateModalVisible(false)}
        onSubmit={handleCreateEstimate}
        loading={creatingEstimate}  // pass the loading state here
      />
    </div>
  );
};

export default FreeAgentProjectsPage;
